/*
SOURCE: screens/StatementScreen/components/Charge.tsx
*/
import React from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

import { chargeDataFragment } from 'apollo/queries/autogenerate/operations';
import { Charge, ChargeKind, Tier } from 'apollo/queries/autogenerate/schemas';
import { AMZ_SUB_MERCHANTS_IDS } from 'constants/amazon';
import Icon, { ICON_MAP } from 'shared/modules/common/components/atoms/Icon';
import generateID from 'shared/modules/common/utils/generate-id';
import {
  getApplePaymentChargeTitle,
  getCurrentInstallmentIndex,
  getTotalInstallment,
  isApplePayment,
  toFullWidth,
} from 'shared/modules/usage/utils/charge-utils';
import { formatToCurrency, formatYearMonthDate } from 'shared/utils/japanese';
import { convertAmazonPayTitle } from 'utils/amazon-pay-default-storename';

type Props = {
  charge: chargeDataFragment;
  hideMetaDescription?: boolean;
  onPress?: (charge: Charge) => void;
  style?: StyleProp<ViewStyle>;
  id?: string;
};

const ChargeItem = (props: Props) => {
  const charge = props.charge;

  // ------ START APPLE PAYMENT ------
  const isApplePaymentCharge = isApplePayment(charge);
  const applePaymentTitle = isApplePaymentCharge
    ? getApplePaymentChargeTitle(charge)
    : '';
  // ------ END APPLE PAYMENT ------

  // ------ START AMAZON PAYMENT ------
  const isAmazonPaymentCharge =
    charge.payment?.subMerchantId === AMZ_SUB_MERCHANTS_IDS.amazonPay;

  // If the storeName of Amazon Pay purchase starts with 'AMZ', should display default title
  const shouldConvertAmazonPayDefaultTitle =
    !!charge.payment?.storeName &&
    convertAmazonPayTitle(charge.payment?.storeName);

  // If the charged payment is Amazon Pay purchase, should display following title
  const amazonPayTitle = isAmazonPaymentCharge
    ? charge.payment?.storeName === null ||
      charge.payment?.storeName === '<NO NAME>' ||
      charge.payment?.storeName === undefined ||
      shouldConvertAmazonPayDefaultTitle
      ? 'Amazon Pay提携サイト'
      : charge.payment?.storeName
    : '';

  const amazonPayDivisionTitle = isAmazonPaymentCharge ? 'Amazon Pay' : '';
  // ------ END AMAZON PAYMENT ------

  const title = amazonPayTitle || applePaymentTitle || charge.description;
  const subtitle = charge.metadata.byWayOf || charge.payment?.byWayOf;

  const installmentLabel = getInstallmentLabel(charge);
  const iconName = (is3PayNegativeCharge(charge)
    ? 'captured'
    : CHARGE_TYPE_ICON[charge.kind] || 'cart') as keyof typeof ICON_MAP;

  let metaDescription;

  if (!props.hideMetaDescription) {
    if (installmentLabel) {
      metaDescription = installmentLabel;
    } else if (charge.tier === Tier.PLUS) {
      metaDescription = '超あと払い';
    }
  }

  return (
    <TouchableOpacity
      style={[style.root, props.style]}
      onPress={() => props.onPress?.(charge as Charge)}
      /** Generate HTML and Test IDs  */
      {...generateID(props.id)}
      /** Platform-specific props */
      {...Platform.select({
        web: {
          accessibilityRole: 'button',
        },
      })}
    >
      <View style={style.iconWrapper}>
        <Icon name={iconName} size="24" />
      </View>
      <View style={style.leftLabels}>
        <Text style={[style.text, style.textBold]} numberOfLines={1}>
          {title}
        </Text>
        {!!subtitle && (
          <Text style={[style.text, style.textSecondary]}>{subtitle}</Text>
        )}
        {!!amazonPayDivisionTitle && (
          <Text style={[style.text, style.textSecondary]}>
            {amazonPayDivisionTitle}
          </Text>
        )}
        <Text style={[style.text, style.textSecondary]}>
          {formatYearMonthDate(charge.pushedAt, 'en')}
        </Text>
      </View>
      <View style={style.rightLabels}>
        <Text style={[style.text, style.textBold]}>
          {formatToCurrency(charge.amount)}
        </Text>
        {!!metaDescription && (
          <Text style={[style.text, style.textSecondary]}>
            {metaDescription}
          </Text>
        )}
      </View>
    </TouchableOpacity>
  );
};

const is3PayNegativeCharge = (charge: chargeDataFragment) => {
  return (
    charge.kind === ChargeKind.MULTI_PAY_DEBIT &&
    charge.metadata.source.startsWith('isp_') &&
    charge.amount < 0
  );
};

function getInstallmentLabel(charge: chargeDataFragment) {
  if (
    !charge.installmentPlanOptions ||
    !charge.installmentPlanOptions.unavailable.length
  ) {
    return null;
  }

  if (charge.tier === Tier.PLUS) {
    return '超あと払い';
  }

  const currentIndex = getCurrentInstallmentIndex(charge);
  const totalInstallments = getTotalInstallment(charge);

  if (currentIndex < 0 || totalInstallments === 1) {
    return null;
  }

  return `${toFullWidth(currentIndex)}回目/${toFullWidth(
    totalInstallments
  )}回あと払い`;
}

const CHARGE_TYPE_ICON = {
  [ChargeKind.CAPTURE]: 'captured',
  [ChargeKind.LATE_FEE]: 'late-fee',
  [ChargeKind.MANUAL_DEBIT]: 'multi-pay',
  [ChargeKind.MANUAL_CREDIT]: 'multi-pay',
  [ChargeKind.MULTI_PAY_CREDIT]: 'multi-pay',
  [ChargeKind.MULTI_PAY_DEBIT]: 'multi-pay',
  [ChargeKind.REFUND]: 'refund',
  [ChargeKind.CASHBACK]: 'cashback',
  [ChargeKind.SETTLEMENT]: 'settlement',
};

const style = StyleSheet.create({
  root: {
    flexDirection: 'row',
    padding: 16,
  },
  iconWrapper: {
    justifyContent: 'center',
    marginRight: 16,
  },
  text: {
    fontSize: 12,
  },
  textBold: {
    fontWeight: 'bold',
  },
  textSecondary: {
    color: '#586065',
    marginTop: 8,
  },
  leftLabels: {
    flex: 1,
  },
  rightLabels: {
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
});

export default ChargeItem;
